import { useInjected } from '@bubblydoo/angular-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Observable, tap } from 'rxjs'

import { DescriptionDraft } from '@flowx/designer/shared/api/description-draft.types'
import { ResourceDescriptionApiService } from '@flowx/designer/shared/api/resource-description.api'
import { ResourceCloneService } from '@flowx/designer/shared/components/resource-documentation/resource-clone.service'
import { ResourceDocumentationService } from '@flowx/designer/shared/components/resource-documentation/resource-documentation.service'
import { ApplicationResourceType } from '@flowx/designer/shared/entities/application.entity'

export type EdgeDescriptionTarget = {
  resourceType: ApplicationResourceType.Process | ApplicationResourceType.Workflow
  resourceId: string
  elementType: 'sequence' | 'workflowEdge'
  elementId: string
}

export type EdgeDescription = {
  /** null while loading, and when the read failed. */
  description: string | null
  loadFailed: boolean
  reload: () => void
  /** `ownerId` overrides the target's resource id, for a save that follows a clone. */
  save: (description: string, ownerId?: string) => Observable<unknown>
  draft: (() => Observable<DescriptionDraft>) | null
}

export function useEdgeDescription(
  target: EdgeDescriptionTarget,
  open: boolean,
  readOnly: boolean
): EdgeDescription {
  const api = useInjected(ResourceDescriptionApiService)
  const documentation = useInjected(ResourceDocumentationService)
  const resourceClone = useInjected(ResourceCloneService)
  const [description, setDescription] = useState<string | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const { resourceType, resourceId, elementType, elementId } = target

  useEffect(() => {
    setDescription(null)
    setLoadFailed(false)

    if (!open) {
      return
    }

    // A failed read stays distinct from an empty description: editing a blank field there would
    // overwrite text the user never saw.
    const subscription = api
      .getDescription(resourceType, resourceId, { elementType, elementId })
      .subscribe({
        next: (stored) => setDescription(stored.description ?? ''),
        error: () => setLoadFailed(true),
      })

    return () => subscription.unsubscribe()
  }, [api, open, resourceType, resourceId, elementType, elementId, attempt])

  const reload = useCallback(() => setAttempt((count) => count + 1), [])

  const save = useCallback(
    (text: string, ownerId: string = resourceId) =>
      api
        .updateDescription(
          resourceType,
          ownerId,
          { description: text, descriptionBody: null },
          { elementType, elementId }
        )
        .pipe(tap(({ newResourceId }) => resourceClone.follow(ownerId, newResourceId))),
    [api, resourceClone, resourceType, resourceId, elementType, elementId]
  )

  const draft = useMemo(
    () =>
      readOnly
        ? null
        : documentation.draftFor(
            {
              type: 'element',
              resource_id: resourceId,
              resource_type: resourceType,
              element_type: elementType,
              element_id: elementId,
            },
            false
          ),
    [documentation, readOnly, resourceType, resourceId, elementType, elementId]
  )

  return { description, loadFailed, reload, save, draft }
}
